import * as React from "react";
import { SectionShell } from "@/components/design-system/SectionShell";
import { Switch } from "@/components/ui/switch";

const code = `import { Switch } from "@/components/ui/switch"

// Bare control — supply aria-label when there's no visible label
<Switch checked={enabled} onChange={setEnabled} aria-label="Newsletter signup" />

// With a built-in clickable label
<Switch checked={enabled} onChange={setEnabled} label="Newsletter signup" />

// Small size, for dense rows (section editors, tables)
<Switch size="sm" checked={enabled} onChange={setEnabled} />

// Typical admin usage: persist the flip, keep the scroll position
<Switch
  checked={enabled}
  onChange={(next) => router.patch("/admin/features/my_feature", { enabled: next }, { preserveScroll: true })}
  label="My feature"
/>`;

function PreviewGroup() {
  const [a, setA] = React.useState(true);
  const [b, setB] = React.useState(false);
  const [c, setC] = React.useState(true);
  return (
    <div className="flex flex-col gap-4">
      <Switch checked={a} onChange={setA} label="Live on the website (green)" />
      <Switch checked={b} onChange={setB} label="Switched off (red)" />
      <div className="flex items-center gap-4">
        <Switch size="sm" checked={c} onChange={setC} label="Small — for dense rows" />
        <Switch checked={true} disabled label="Disabled" />
      </div>
    </div>
  );
}

export function SwitchSection() {
  return (
    <SectionShell
      id="switch"
      title="Switch"
      description={
        <>
          An on/off control for a yes/no state that takes effect immediately —
          no Save button. The sliding dot doubles as the status light:{" "}
          <strong>green means live on the website, red means switched
          off</strong>. Built as a <code>&lt;button role="switch"&gt;</code>,
          so it never participates in form submission.
        </>
      }
      whenToUse={
        <ul>
          <li>Feature switches and visibility toggles that persist on flip.</li>
          <li>Include/exclude a page or section from the public site.</li>
          <li>Any setting where on/off status should be readable at a glance.</li>
        </ul>
      }
      whenNotToUse={
        <ul>
          <li>
            Inside a form that saves on submit — use <code>&lt;Checkbox&gt;</code>.
          </li>
          <li>
            Multi-option choices — use <code>&lt;Radio&gt;</code>,{" "}
            <code>&lt;Select&gt;</code> or toggle buttons.
          </li>
          <li>For primary actions — use <code>&lt;Button&gt;</code>.</li>
        </ul>
      }
      preview={<PreviewGroup />}
      code={code}
      options={
        <ul className="list-disc pl-5">
          <li>
            <code>checked</code> + <code>onChange(next: boolean)</code> —
            controlled only; there is no uncontrolled mode.
          </li>
          <li>
            <code>size</code> — <code>"md"</code> (default) or{" "}
            <code>"sm"</code> for dense rows.
          </li>
          <li>
            <code>label</code> — optional clickable trailing label. Without
            it, pass <code>aria-label</code>.
          </li>
          <li>
            <code>disabled</code> — dims the control and blocks flips.
          </li>
          <li>
            Colors come from the <code>success</code>/<code>danger</code>{" "}
            token triples — never hardcode the green/red.
          </li>
        </ul>
      }
    />
  );
}
